import { useMemo, useState } from 'react';
import { useForm, useStore } from '@tanstack/react-form';
import {
  Button,
  Select,
  SelectItem,
  TextArea,
  TextInput,
  Toggle,
} from '@carbon/react';
import { Copy, Edit, TrashCan } from '@carbon/react/icons';
import { useLocation } from 'react-router-dom';
import { PageHeader } from 'components/page-header';
import {
  ActivityFeedPanel,
  ActivityFeedPanelOpenButton,
} from 'components/activity-feed-panel';
import { FreeTextComboBox } from 'components/free-text-combobox';
import { usePricingEnums } from 'pages/pricing/shared/use-pricing-enums';
import { LegCompositionEditor } from 'pages/pricing/shared/leg-composition-editor';
import { NumericField } from 'pages/pricing/shared/numeric-field';
import { parseUTCdateTime } from 'utils/dates';
import type {
  Curve,
  CurveOption,
  Leg,
  Option,
} from 'pages/pricing/shared/types';
import styles from './curve-detail.module.scss';
import card from './curve-management.module.scss';

export interface CurveFormData {
  name: string;
  market: string;
  units: string;
  currency: string;
  frequency: string;
  category: string;
  tradingCalendar: string;
  status: string;
  resolution: string;
  exchange: string;
  symbol: string;
  priceFallback: string;
  volatilityFallback: string;
  owner: string;
  source: string;
  notes: string;
  derivedPrice: boolean;
  derivedVolatility: boolean;
  priceLegs: Leg[];
  volLegs: Leg[];
}

const labelOf = (opts: Option[], value: string) =>
  opts.find((o) => o.value === value)?.label ?? value;

interface Props {
  initial: CurveFormData;
  isNew: boolean;
  curveId?: number; // the persisted curve id (activity feed target); undefined for a new draft
  updatedAt?: string | null; // for the "· Updated {date}" subtitle
  curveOptions: CurveOption[]; // peer curves (numeric id) for fallback + legs
  otherCurves: Curve[]; // peers (excluding this) for symbol uniqueness
  exchangeOptions: string[]; // distinct venues already in use, for the Exchange typeahead
  onSave: (payload: Partial<Curve>, isNew: boolean) => Promise<Curve>;
  onBack: () => void;
  onRequestDelete: () => void;
  onRequestClone: () => void;
}

// Backend timestamps are UTC; format them in UTC (parseUTCdateTime) so the date can't drift a day
// near midnight in the viewer's local zone.
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  return parseUTCdateTime(iso);
};

export function CurveDetail({
  initial,
  isNew,
  curveId,
  updatedAt,
  curveOptions,
  otherCurves,
  exchangeOptions,
  onSave,
  onBack,
  onRequestDelete,
  onRequestClone,
}: Props) {
  const { enums } = usePricingEnums();
  const { pathname } = useLocation();
  const [mode, setMode] = useState<'view' | 'edit'>(isNew ? 'edit' : 'view');
  const readOnly = mode === 'view';

  const form = useForm({
    defaultValues: initial,
    onSubmit: async ({ value }) => {
      const toId = (s: string) => (s === '' ? null : Number(s));
      const payload: Partial<Curve> = {
        name: value.name.trim() || 'Untitled curve',
        market: value.market,
        units: value.units,
        currency: value.currency,
        frequency: value.frequency,
        category: value.category,
        tradingCalendar: value.tradingCalendar,
        status: value.status,
        resolution: value.resolution,
        exchange: value.exchange,
        symbol: value.symbol,
        priceFallback: toId(value.priceFallback),
        volatilityFallback: toId(value.volatilityFallback),
        owner: value.owner,
        source: value.source,
        notes: value.notes,
        priceComposition: { legs: value.derivedPrice ? value.priceLegs : [] },
        volatilityComposition: {
          legs: value.derivedVolatility ? value.volLegs : [],
        },
      };
      await onSave(payload, isNew);
      // On create the parent re-mounts us on the saved curve (view mode); on update, rebase here.
      if (!isNew) {
        form.reset(value);
        setMode('view');
      }
    },
  });

  const values = useStore(form.store, (s) => s.values);
  const { isSubmitting } = useStore(form.store, (s) => ({
    isSubmitting: s.isSubmitting,
  }));

  // Symbol: required unless derived; 2–8 alphanumerics; unique per exchange. (Edit mode only.)
  const symbolError = (() => {
    if (readOnly || values.derivedPrice) return '';
    const s = values.symbol.trim();
    if (!s) return 'Symbol is required.';
    if (!/^[A-Z0-9]{2,8}$/.test(s)) return 'Use 2–8 letters or numbers.';
    const dup = otherCurves.some(
      (c) =>
        (c.exchange ?? '') === values.exchange &&
        (c.symbol ?? '').toUpperCase() === s.toUpperCase()
    );
    return dup
      ? `Symbol already used on ${values.exchange || 'this venue'}.`
      : '';
  })();

  // A required name; the nameError string both blocks Save and drives the field's red-border
  // /invalidText cue (matching the symbol field + the contract/transformation editors).
  const nameError =
    !readOnly && values.name.trim() === '' ? 'Enter a name to save.' : '';

  const canSave =
    !nameError &&
    !symbolError &&
    !(values.derivedPrice && values.priceLegs.length === 0) &&
    !(values.derivedVolatility && values.volLegs.length === 0);

  // Fallback pickers are Carbon Selects (string values); map the numeric curve ids to strings.
  const fallbackOptions: Option[] = curveOptions.map((o) => ({
    value: String(o.value),
    label: o.label,
  }));

  // Exchange isn't an enum — a curve can land on a venue nobody's used yet — so it's a
  // FreeTextComboBox: pick a venue already in use, or type a brand new one.
  const exchangeItems: Option[] = useMemo(
    () => exchangeOptions.map((e) => ({ value: e, label: e })),
    [exchangeOptions]
  );

  // ── field helpers ──────────────────────────────────────────────
  const rtext = (
    id: string,
    label: string,
    field: keyof CurveFormData,
    opts: { upper?: boolean; error?: string } = {}
  ) => (
    <TextInput
      id={id}
      labelText={label}
      value={values[field] as string}
      readOnly={readOnly}
      invalid={!readOnly && !!opts.error}
      invalidText={opts.error}
      onChange={(e) =>
        form.setFieldValue(
          field,
          opts.upper ? e.target.value.toUpperCase() : e.target.value
        )
      }
    />
  );

  const rselect = (
    id: string,
    label: string,
    field: keyof CurveFormData,
    options: Option[]
  ) => {
    if (readOnly) {
      return (
        <TextInput
          id={id}
          labelText={label}
          value={labelOf(options, values[field] as string) || '—'}
          readOnly
        />
      );
    }
    return (
      <Select
        id={id}
        labelText={label}
        value={values[field] as string}
        onChange={(e) => form.setFieldValue(field, e.target.value)}
      >
        <SelectItem value="" text="—" />
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} text={o.label} />
        ))}
      </Select>
    );
  };

  const title = isNew ? values.name.trim() || 'New curve' : initial.name;
  const subtitle = [
    values.symbol,
    values.exchange,
    labelOf(enums?.commodities ?? [], values.market),
    values.units,
    updatedAt ? `Updated ${fmtDate(updatedAt)}` : '',
  ]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <main className={styles.page}>
      <PageHeader
        breadcrumbs={[
          ['/pricing/curve-management', 'Curve Management'],
          [pathname, title],
        ]}
        title={title}
        titleDetail={
          !isNew && subtitle ? (
            <div className={styles.subtitle}>{subtitle}</div>
          ) : null
        }
        actions={
          <div className={styles.actions}>
            {!isNew && <ActivityFeedPanelOpenButton />}
            {readOnly ? (
              <>
                <Button
                  kind="danger--tertiary"
                  size="md"
                  renderIcon={TrashCan}
                  onClick={onRequestDelete}
                >
                  Delete
                </Button>
                <Button
                  kind="tertiary"
                  size="md"
                  renderIcon={Copy}
                  onClick={onRequestClone}
                >
                  Clone
                </Button>
                <Button
                  kind="primary"
                  size="md"
                  renderIcon={Edit}
                  onClick={() => setMode('edit')}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  kind="secondary"
                  size="md"
                  onClick={() => {
                    if (isNew) {
                      onBack();
                    } else {
                      form.reset(initial);
                      setMode('view');
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  size="md"
                  disabled={!canSave || isSubmitting}
                  onClick={() => form.handleSubmit()}
                >
                  {isSubmitting
                    ? 'Saving…'
                    : isNew
                      ? 'Create curve'
                      : 'Save changes'}
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className={styles.scroll}>
        {/* Identity */}
        <section className={card.section}>
          <h2 className={card.sectionTitle}>Identity</h2>
          <div className={card.grid3}>
            {rtext('cd-name', 'Curve name', 'name', { error: nameError })}
            <TextInput
              id="cd-symbol"
              labelText="Symbol"
              value={values.symbol}
              readOnly={readOnly}
              maxLength={8}
              invalid={Boolean(symbolError)}
              invalidText={symbolError}
              onChange={(e) =>
                form.setFieldValue('symbol', e.target.value.toUpperCase())
              }
            />
            {readOnly ? (
              <TextInput
                id="cd-exchange"
                labelText="Exchange"
                value={values.exchange}
                readOnly
              />
            ) : (
              <FreeTextComboBox
                id="cd-exchange"
                titleText="Exchange"
                placeholder="Select or type an exchange"
                items={exchangeItems}
                selectedItem={
                  values.exchange
                    ? { value: values.exchange, label: values.exchange }
                    : null
                }
                itemToString={(item) => item?.label ?? ''}
                onChange={({ selectedItem }) =>
                  form.setFieldValue('exchange', selectedItem?.value ?? '')
                }
              />
            )}
            {rselect(
              'cd-market',
              'Commodity',
              'market',
              enums?.commodities ?? []
            )}
            {rselect(
              'cd-category',
              'Category',
              'category',
              enums?.categories ?? []
            )}
          </div>
        </section>

        {/* Pricing */}
        <section className={card.section}>
          <h2 className={card.sectionTitle}>Pricing</h2>
          <div className={card.grid3}>
            {rselect('cd-units', 'Price unit', 'units', enums?.units ?? [])}
            {rselect(
              'cd-currency',
              'Currency',
              'currency',
              enums?.currencies ?? []
            )}
            {rselect(
              'cd-freq',
              'Frequency',
              'frequency',
              enums?.frequencies ?? []
            )}
            {readOnly ? (
              <TextInput
                id="cd-res"
                labelText="Resolution (min price movement)"
                value={values.resolution}
                readOnly
              />
            ) : (
              <NumericField
                id="cd-res"
                label="Resolution (min price movement)"
                allowNegative={false}
                min={0}
                value={Number(Number(values.resolution || 0.01).toFixed(6))}
                onValueChange={(value) =>
                  form.setFieldValue(
                    'resolution',
                    String(Number(value.toFixed(6)))
                  )
                }
              />
            )}
          </div>

          <div className={card.derivedBlock}>
            <div className={`${card.sectionHint} ${card.fallbackHint}`}>
              Fallback — used when this curve has no marks for a requested
              tenor.
            </div>
            {rselect(
              'cd-pfb',
              'Price fallback curve',
              'priceFallback',
              fallbackOptions
            )}
          </div>

          <Toggle
            id="cd-derived-price"
            size="sm"
            labelText="Derived price curve"
            labelA="Off"
            labelB="On"
            toggled={values.derivedPrice}
            disabled={readOnly}
            onToggle={(v) => form.setFieldValue('derivedPrice', v)}
          />
          {values.derivedPrice && (
            <div className={card.derivedBlock}>
              <LegCompositionEditor
                legs={values.priceLegs}
                curveOptions={curveOptions}
                onChange={(legs) => form.setFieldValue('priceLegs', legs)}
                formulaName={values.symbol || values.name || 'Curve'}
                readOnly={readOnly}
              />
            </div>
          )}
        </section>

        {/* Volatility */}
        <section className={card.section}>
          <h2 className={card.sectionTitle}>Volatility</h2>
          <div className={card.derivedBlock}>
            {rselect(
              'cd-vfb',
              'Volatility fallback surface',
              'volatilityFallback',
              fallbackOptions
            )}
          </div>
          <Toggle
            id="cd-derived-vol"
            size="sm"
            labelText="Derived volatility surface"
            labelA="Off"
            labelB="On"
            toggled={values.derivedVolatility}
            disabled={readOnly}
            onToggle={(v) => form.setFieldValue('derivedVolatility', v)}
          />
          {values.derivedVolatility && (
            <div className={card.derivedBlock}>
              <LegCompositionEditor
                legs={values.volLegs}
                curveOptions={curveOptions}
                onChange={(legs) => form.setFieldValue('volLegs', legs)}
                formulaLabel="Combined surface formula"
                formulaName={`${values.symbol || values.name || 'Curve'} vol`}
                readOnly={readOnly}
              />
            </div>
          )}
        </section>

        {/* Calendar */}
        <section className={card.section}>
          <h2 className={card.sectionTitle}>Calendar</h2>
          <div className={card.grid2}>
            {rselect(
              'cd-calendar',
              'Trading calendar',
              'tradingCalendar',
              enums?.tradingCalendars ?? []
            )}
          </div>
        </section>

        {/* Governance */}
        <section className={card.section}>
          <h2 className={card.sectionTitle}>Governance</h2>
          <div className={card.grid2}>
            {rselect('cd-status', 'Status', 'status', enums?.statuses ?? [])}
            {rtext('cd-owner', 'Owner', 'owner')}
            {rtext('cd-source', 'Source', 'source')}
            <TextArea
              id="cd-notes"
              labelText="Notes"
              rows={3}
              value={values.notes}
              readOnly={readOnly}
              onChange={(e) => form.setFieldValue('notes', e.target.value)}
            />
          </div>
        </section>
      </div>

      {!isNew && curveId !== undefined && (
        <ActivityFeedPanel
          modelType="Pricing::Curve"
          modelId={curveId}
          title="Activity"
          subtitle={values.name || 'Curve'}
          commentsHref={`/pricing/curves/${curveId}/comments`}
        />
      )}
    </main>
  );
}
